import React, { useContext } from "react";
import { GetStaticProps } from "next";
import Head from "next/head";
import Link from "next/link";
import PageLayoutV1 from "@app/hocs/PageLayoutV1";
import { META_DESCRIPTION, META_URL, REVALIDATE_SECONDS } from "@app/utilities/constants";
import PaginationComp from "@components/navs/PaginationComp";
import { IPaginationBase } from "@models/interfaces/IPagination";
import { LocationContext } from "@app/contexts/LocationContext";

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ILocationsPage {}

const ContactsPage: React.FC<ILocationsPage> = () => {
  const { locations: geolocations } = useContext(LocationContext),
    pagination: IPaginationBase = {
      page: 1,
      rowsPerPage: 100,
      totalCount: geolocations.length,
    };

  /**
   * Meta contents
   */
  const metaTitle = `Locations | Heritage Counseling`,
    metaDescription = META_DESCRIPTION || "",
    metaUrl = `${META_URL}/locations`,
    metaImage = `${META_URL}/logo.png`;

  return (
    <PageLayoutV1 title="Locations" fluidContainer={false} noPadding={false}>
      <Head>
        <title>{metaTitle}</title>
        <meta key="title" name="title" content={metaTitle} />
        <meta key="description" name="description" content={metaDescription} />

        <meta key="og:title" property="og:title" content={metaTitle} />
        <meta key="og:description" property="og:description" content={metaDescription} />
        <meta key="og:url" property="og:url" content={metaUrl} />
        <meta key="og:image" property="og:image" content={metaImage} />

        <meta key="twitter:url" name="twitter:url" content={metaUrl} />
        <meta key="twitter:title" name="twitter:title" content={metaTitle} />
        <meta key="twitter:description" name="twitter:description" content={metaDescription} />
        <meta key="twitter:image" name="twitter:image" content={metaImage} />

        <meta key="prop:title" itemProp="title" content={metaTitle} />
        <meta key="prop:description" itemProp="description" content={metaDescription} />

        <link rel="canonical" href={metaUrl} />
        {geolocations?.length > 0 &&
          geolocations.map((geolocation) => (
            <script
              key={geolocation.slug}
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(geolocation.schema),
              }}
            ></script>
          ))}
      </Head>
      <div className="locations-page" data-testid="locations-page">
        <div className="locations-list">
          {geolocations.map((loc, idx) => {
            const addressCityState = [loc?.city, [loc?.state, loc?.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");

            return (
              <Link key={idx} href={`/locations/${loc.slug}`}>
                <a className="card blog-card">
                  <div className="row">
                    <div className="col-md-4 col-xl-3 img-container">
                      <img className="img-blog" src={loc.image?.src || ""} alt={loc.name} height={150} width={150} />
                    </div>
                    <div className="col-md-8 col-xl-9">
                      <div className="card-body">
                        <h5 className="card-title">{loc.name}</h5>
                        <address>
                          {loc.address.map((address, index) => (
                            <div key={index}>
                              <span className="address-line">{address}</span>
                            </div>
                          ))}
                          <div>
                            <span className="address-city-state">{addressCityState}</span>
                          </div>
                        </address>
                      </div>
                    </div>
                  </div>
                </a>
              </Link>
            );
          })}
          {/* <div className="pagination-container">
            <PaginationComp pagination={pagination} />
          </div> */}
        </div>
      </div>
    </PageLayoutV1>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {} as ILocationsPage,
    revalidate: REVALIDATE_SECONDS, // In seconds
  };
};

export default ContactsPage;
